import { CommandJob, Job } from "~/components/build/Show/lib/types";
import { totalAnnotationCount } from "~/components/build/Show/components/AnnotationsBadge";
import TestEngine from "~/components/build/Show/components/TestEngine";
import FailedTestExecutionsIndicator from "~/components/job/Job/FailedTestExecutionsIndicator";
import JobActivityTimeline from "~/components/JobActivityTimeline";
import JobAnnotations from "~/components/job/Job/Annotations";
import JobAnnotationsCount from "~/components/job/Job/AnnotationsCount";
import JobArtifacts from "~/components/job/Job/Artifacts";
import JobArtifactsCount from "~/components/job/Job/ArtifactsCount";
import JobEnvironment from "~/components/job/Job/Environment";
import JobLog from "~/components/job/Job/Log";
import AgentLog from "~/components/job/Job/AgentLog";
import AgentTab, { AgentTabLabel } from "~/components/job/Job/AgentTab";
import Packages from "~/components/job/Job/Packages";
import HostedCompute from "~/components/job/Job/HostedCompute";

export type JobTabId =
  | "annotations"
  | "agent"
  | "output"
  | "agent-log"
  | "tests-v2"
  | "packages"
  | "artifacts"
  | "timeline"
  | "env"
  | "hosted-compute";

export type JobTab = {
  id: JobTabId;
  name: React.ReactNode | ((job: CommandJob) => React.ReactNode);
  component: React.ElementType;
  indicator?: React.ElementType;
  count?: React.ElementType;
  new?: boolean;
  enabled: boolean;
};

/**
 * Returns the list of available tabs for a given user/job.
 */
export function getJobTabs(job: Job): JobTab[] {
  // Only CommandJob (type === "script") has full tab support
  if (job.type !== "script") {
    return [
      {
        id: "output",
        name: "Log",
        component: JobLog,
        enabled: true,
      },
    ];
  }

  const tabs: Array<JobTab> = [
    {
      id: "annotations",
      name: "Annotations",
      count: JobAnnotationsCount,
      component: JobAnnotations,
      enabled: Boolean(Features.BuildSidebar),
    },
    {
      id: "agent",
      name: () => AgentTabLabel({ job }),
      component: AgentTab,
      enabled: Boolean(Features.BuildSidebar && Features.BuildJobAgentTab),
    },
    {
      id: "output",
      name: "Log",
      component: JobLog,
      enabled: true,
    },
    {
      id: "agent-log",
      name: "Agent Log",
      component: AgentLog,
      enabled: Boolean(
        job.hosted && job.permissions?.viewAgentLog && window.Features.AgentLogViewer,
      ),
    },
    {
      id: "tests-v2",
      name: "Tests",
      indicator: FailedTestExecutionsIndicator,
      component: TestEngine,
      enabled: Boolean(
        !window.Features.TestEngineDisableBuildPageTestTab && job.testEngine.testTabUrls?.tabUrl,
      ),
    },
    {
      id: "packages",
      name: "Packages",
      new: true,
      component: Packages,
      enabled: Boolean(window.Features.PackagesPipelinesBuildJobTab && job.packages?.indexUrl),
    },
    {
      id: "artifacts",
      name: "Artifacts",
      count: JobArtifactsCount,
      component: JobArtifacts,
      enabled: true,
    },
    {
      id: "timeline",
      name: "Timeline",
      component: JobActivityTimeline,
      enabled: true,
    },
    {
      id: "env",
      name: "Environment",
      component: JobEnvironment,
      enabled: true,
    },
    {
      id: "hosted-compute",
      name: "Metrics",
      component: HostedCompute,
      enabled: Boolean(job.startedAt && job.hostedAgentMetricsPath),
    },
  ];

  return tabs.filter((tab) => tab.enabled);
}

/**
 * Returns the default tab ID for a given job.
 * Annotations tab is default if annotations exist, otherwise output.
 */
export function getDefaultJobTab(job: Job): JobTabId {
  if (
    isTabAvailable(job, "annotations") &&
    job.type === "script" &&
    totalAnnotationCount(job.statistics?.annotationCountsByStyle || {}) > 0
  ) {
    return "annotations";
  }
  return "output";
}

/**
 * Checks if a tab available for the given job.
 */
export function isTabAvailable(job: Job, tabId: JobTabId | string) {
  const validTabs = getJobTabs(job);
  return validTabs.some((tab) => tab.id === tabId && tab.enabled);
}
